import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../../../environments/environment';

/**
 * Protects routes that require authentication (e.g., /panel).
 * If the user has no session, sends them to WordPress — the only entry
 * point to the panel is via the WordPress "Mi cuenta" link with ?access_token=XXX.
 */
export const authGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // OAuth popup returning from Cognito redirect (redirectSignIn → /panel/subscription).
  // Redirect to the spinner page so the user never sees the full panel UI.
  // The Hub 'signedIn' handler will sync the profile, save the session and close the popup.
  try {
    if (localStorage.getItem('eld_oauth_popup') === '1') {
      return router.createUrlTree(['/oauth-popup']);
    }
  } catch { /* no-op */ }

  const authenticated = await authService.loadSessionToken();
  if (!authenticated) {
    window.location.href = environment.wordpressUrl;
    return false;
  }
  return true;
};

/**
 * Protects guest pages (/login, /register, /forgot-password).
 *
 * These pages are ONLY meant to be used inside the WordPress modal iframe.
 * Direct access (no iframe) is redirected to WordPress.
 *
 * In embed mode: always passes through and lets the component handle
 * the "already authenticated" case via ngOnInit → postEmbeddedSessionToParent().
 */
export const noAuthGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const embed = route.queryParamMap.get('embed');

  // Embed mode: ?embed=1 in URL (first load) OR already inside an iframe
  // (internal navigations /login → /register lose the query param but stay in iframe).
  const isEmbed = embed === '1' || embed === 'true' || window.self !== window.top;

  // En /login se agrega ?eld_login=1 para que events.js abra el modal de login
  // al cargar WordPress (enlaces de correos, ej. "Cuenta creada" → {link_login}).
  if (!isEmbed) {
    const base = environment.wordpressUrl.replace(/\/+$/, '');
    window.location.href = route.routeConfig?.path === 'login' ? `${base}/?eld_login=1` : base;
    return false;
  }

  // In embed mode: always allow — the component handles the session detection.
  return true;
};

/**
 * Catch-all guard: redirects any unrecognised or direct-access route to WordPress.
 */
export const wordpressRedirectGuard: CanActivateFn = () => {
  window.location.href = environment.wordpressUrl;
  return false;
};

/**
 * Handles /logout route: called by WordPress when the user logs out there.
 * Clears the Angular/Cognito session then redirects to the returnUrl (WordPress page).
 * Security: only redirects to the trusted WordPress origin to prevent open redirect.
 */
export const logoutGuard: CanActivateFn = async (route) => {
  const authService = inject(AuthService);
  const raw = route.queryParamMap.get('returnUrl') || '';

  // Validate returnUrl — only allow redirects to the trusted WordPress origin
  let returnUrl = environment.wordpressUrl;
  try {
    const u = new URL(raw);
    const wp = new URL(environment.wordpressUrl);
    if (u.origin === wp.origin) returnUrl = raw;
  } catch (_) {}

  await authService.logout();
  // Append ?px_auth=logout so events.js cleans lacronica_session on the WP domain.
  const sep = returnUrl.includes('?') ? '&' : '?';
  window.location.href = returnUrl + sep + 'px_auth=logout';
  return false;
};
